import { Component, Input, OnChanges } from '@angular/core';
import { ChartTypeOption } from '../chart-type-toggle/chart-type-toggle.component';
import { ReportColumn } from '../data-table/data-table.component';
import { formatSharePercent } from '../../utils/chart-helpers';
import { CHART_COLORS, colorByIndex, formatChartValue, prepareChartData } from '../../utils/chart-theme';

const DONUT_TYPES: ChartTypeOption[] = [
  { id: 'donut', label: 'Donut' },
  { id: 'pie', label: 'Pie' },
  { id: 'bar-h', label: 'Bar' },
  { id: 'table', label: 'Table' }
];

const GRID_STROKE = '#dee2e6';
const TICK_MUTED = '#98a6ad';
const TICK_BODY = '#323a46';

@Component({
  selector: 'app-br-donut-chart',
  templateUrl: './donut-chart.component.html',
  host: { '[attr.title]': 'null' }
})
export class BrDonutChartComponent implements OnChanges {
  @Input() chartTitle: string;
  /** Short line under the title explaining how the chart values are calculated. */
  @Input() chartDescription: string;
  @Input() items: any[] = [];
  @Input() segments: any[];
  @Input() valueFormatter: (v: number) => string;
  @Input() maxItems = 8;
  @Input() emptyLabel = 'No data';
  @Input() colors: string[] = CHART_COLORS;
  /** When set, donut center and legend % use this instead of the sum of slice values. */
  @Input() centerTotal: number;

  readonly chartTypeOptions = DONUT_TYPES;
  chartType = 'table';
  data: any[] = [];
  total = 0;

  chartLabels: string[] = [];
  chartDatasets: any[] = [];
  chartOptions: any = {};
  barChartLabels: string[] = [];
  barChartDatasets: any[] = [];
  barChartOptions: any = {};
  tableColumns: ReportColumn[] = [];

  ngOnChanges() {
    const source = this.segments || this.items;
    this.data = prepareChartData(source, 'value', 'label', this.maxItems);
    this.total = this.data.reduce((sum, item) => sum + item.value, 0);
    this.tableColumns = this.buildTableColumns();
    this.rebuildChart();
  }

  /** Values are already percentages (e.g. Timeliness — %) — skip redundant % of Total column. */
  private get isPercentChart(): boolean {
    if (!this.valueFormatter || !this.data.length) return false;
    const sum = this.total;
    return sum > 0 && sum <= 100.5 && this.data.every((item) => item.value >= 0 && item.value <= 100);
  }

  private buildTableColumns(): ReportColumn[] {
    const columns: ReportColumn[] = [
      { key: 'label', header: 'Category' },
      {
        key: 'value',
        header: 'Value',
        value: true,
        render: (row) => this.formattedValue(row.value)
      }
    ];

    if (!this.isPercentChart) {
      columns.push({
        key: 'percent',
        header: '% of Total',
        value: true,
        render: (row) => `${this.legendPercent(row.value)}%`
      });
    }

    return columns;
  }

  onChartTypeChange(type: string) {
    this.chartType = type;
    this.rebuildChart();
  }

  legendPercent(value: number): string {
    const base = this.centerTotal != null ? this.centerTotal : this.total;
    return formatSharePercent(value, base);
  }

  formattedValue(value: number): string {
    return formatChartValue(value, this.valueFormatter);
  }

  entryColor(entry: any, index: number): string {
    return entry.color || colorByIndex(index, this.colors);
  }

  get centerValue(): string {
    const value = this.centerTotal != null ? this.centerTotal : this.total;
    return formatChartValue(value, this.valueFormatter);
  }

  private rebuildChart() {
    this.chartLabels = this.data.map((d) => d.label);
    const values = this.data.map((d) => d.value);
    const backgroundColor = this.data.map((d, i) => this.entryColor(d, i));

    this.chartDatasets = [{ data: values, backgroundColor, borderWidth: 0 }];
    this.chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      cutoutPercentage: this.chartType === 'pie' ? 0 : 62,
      legend: { display: false },
      tooltips: {
        callbacks: {
          title: () => null,
          label: (tooltipItem: any, data: any) => {
            const value = data.datasets[0].data[tooltipItem.index];
            const label = data.labels[tooltipItem.index];
            return `${label}: ${this.formattedValue(value)}`;
          }
        }
      }
    };

    this.barChartLabels = this.chartLabels;
    this.barChartDatasets = [{ data: values, backgroundColor: this.colors[0] }];
    this.barChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      legend: { display: false },
      tooltips: {
        callbacks: {
          title: () => null,
          label: (tooltipItem: any) => {
            const category = this.barChartLabels[tooltipItem.index];
            const value = this.formattedValue(Number(tooltipItem.xLabel));
            return category ? `${category}: ${value}` : value;
          }
        }
      },
      scales: {
        xAxes: [{ gridLines: { color: GRID_STROKE, drawBorder: false }, ticks: { fontColor: TICK_MUTED, fontSize: 12, beginAtZero: true } }],
        yAxes: [{ gridLines: { display: false, drawBorder: false }, ticks: { fontColor: TICK_BODY, fontSize: 12 } }]
      }
    };
  }

  get barChartHeight(): number {
    return Math.max(220, this.data.length * 42 + 40);
  }
}
